/**
 * DiscoverPage — Token discovery with grid, search, filters, and sort.
 *
 * All filter state is URL-driven via TanStack Router search params.
 * This ensures shareable/bookmarkable URLs and browser history support.
 *
 * Story 3.4 — Task 5
 */

import type { TokenListItem } from "@repo/shared-types";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useCallback, useMemo } from "react";

import { CategoryFilter } from "../components/discover/CategoryFilter";
import { SortDropdown } from "../components/discover/SortDropdown";
import { TokenGrid } from "../components/discover/TokenGrid";
import { TokenSearch } from "../components/discover/TokenSearch";
import { TrendingStrip } from "../components/discover/TrendingStrip";
import { VerificationFilter } from "../components/discover/VerificationFilter";
import { type TokenFilters, useTokens } from "../hooks/api/use-tokens";
import { useTrending } from "../hooks/use-trending";
import { fadeInUp } from "../lib/animations";

/**
 * Safely extract a string value from the untyped search params object.
 * Works with TanStack Router's `useSearch({ strict: false })` return type
 * without requiring type assertions.
 */
function getSearchParam(search: unknown, key: string): string | undefined {
	if (typeof search !== "object" || search === null) return undefined;
	for (const [k, v] of Object.entries(search)) {
		if (k === key && typeof v === "string") return v;
	}
	return undefined;
}

export function DiscoverPage() {
	const rawSearch = useSearch({ strict: false });
	const navigate = useNavigate();

	const searchText = getSearchParam(rawSearch, "search");
	const category = getSearchParam(rawSearch, "category");
	const sort = getSearchParam(rawSearch, "sort");
	const verification = getSearchParam(rawSearch, "verification");

	const filters: TokenFilters = useMemo(() => {
		const f: TokenFilters = {
			sort: sort ?? "newest",
			order: "desc",
		};
		if (searchText) f.search = searchText;
		if (category) f.category = category;
		if (verification) f.verification = verification;
		return f;
	}, [searchText, category, sort, verification]);

	const { data, isLoading, error, isFetchingNextPage, hasNextPage, fetchNextPage } =
		useTokens(filters);

	const trending = useTrending();

	const tokens: TokenListItem[] = useMemo(
		() => data?.pages.flatMap((page) => page.items) ?? [],
		[data]
	);

	const hasActiveFilters = !!(searchText || category || verification);

	const updateParam = useCallback(
		(key: string, value: string | undefined) => {
			const next: Record<string, string> = {};
			// Rebuild from current extracted params (avoids window.location.search)
			if (searchText) next.search = searchText;
			if (category) next.category = category;
			if (sort && sort !== "newest") next.sort = sort;
			if (verification) next.verification = verification;

			// Apply the change (skip default sort to keep URL clean)
			if (value && !(key === "sort" && value === "newest")) {
				next[key] = value;
			}
			navigate({
				to: "/discover",
				search: next,
				replace: true,
			});
		},
		[navigate, searchText, category, sort, verification]
	);

	const handleSearchChange = useCallback(
		(value: string) => updateParam("search", value || undefined),
		[updateParam]
	);

	const handleCategoryChange = useCallback(
		(cat: string | undefined) => updateParam("category", cat),
		[updateParam]
	);

	const handleSortChange = useCallback((s: string) => updateParam("sort", s), [updateParam]);

	const handleVerificationChange = useCallback(
		(checked: boolean) => updateParam("verification", checked ? "verified" : undefined),
		[updateParam]
	);

	const handleClearFilters = useCallback(
		() =>
			navigate({
				to: "/discover",
				replace: true,
			}),
		[navigate]
	);

	return (
		<motion.div
			variants={fadeInUp}
			initial="hidden"
			animate="visible"
			className="mx-auto max-w-7xl px-4 py-8"
		>
			<div className="mb-8">
				<h1 className="text-3xl font-bold text-pastel-black">Discover Tokens</h1>
				<p className="mt-2 text-pastel-gray">
					Browse and discover community tokens launched on the platform.
				</p>
			</div>

			{/* Controls */}
			<div className="space-y-4 mb-6">
				<div className="flex flex-col sm:flex-row gap-3">
					<div className="flex-1">
						<TokenSearch value={searchText ?? ""} onChange={handleSearchChange} />
					</div>
					<div className="flex items-center gap-3">
						<SortDropdown value={sort ?? "newest"} onChange={handleSortChange} />
						<VerificationFilter
							checked={verification === "verified"}
							onChange={handleVerificationChange}
						/>
					</div>
				</div>
				<CategoryFilter value={category} onChange={handleCategoryChange} />
			</div>

			{/* Trending section — hidden on error */}
			{!trending.error && (
				<TrendingStrip tokens={trending.data?.data.items ?? []} isLoading={trending.isLoading} />
			)}

			{/* Error state */}
			{error && !isLoading && (
				<div className="rounded-lg border border-red-200 bg-red-50 p-4 mb-6" role="alert">
					<p className="text-sm font-medium text-red-800">Failed to load tokens</p>
					<p className="mt-1 text-sm text-red-700">Something went wrong. Please try again later.</p>
				</div>
			)}

			{/* Grid */}
			<TokenGrid
				tokens={tokens}
				isLoading={isLoading}
				isFetchingNextPage={isFetchingNextPage}
				hasNextPage={hasNextPage ?? false}
				fetchNextPage={fetchNextPage}
				onClearFilters={handleClearFilters}
				hasActiveFilters={hasActiveFilters}
			/>
		</motion.div>
	);
}
